import { Controller } from "@hotwired/stimulus";
import RequestHelper from "../helpers/request_helper";

// Mounts on gives/redemptions/_edit_banner_modal.html.haml, rendered only from
// gives/redemptions/show.html.haml. #redemption-form and .edit-redemption-image-modal live outside
// this controller's element, so they stay bare lookups rather than targets.
//
// The whole modal is mobile-only: its .btn-edit-banner trigger renders only behind a non-legacy
// Design::HeroImage and is `display: none` above $screen-xs-max.
//
// The .switch-to-mobile / .switch-to-desktop pills are dead - both render with a hard-coded
// `.hidden` class nothing ever removes, per edit-banner-modal.scss ("we are not going to support
// mobile for the moment"). switchToDesktop is reached only from onEditBannerModalOpen.
const MIN_HERO_HEIGHT = 300;
const DEFAULT_HERO_HEIGHT = 700;
const DEFAULT_DESKTOP_WIDTH = 1333;

const parsePropsInt = (prop, heroImage) =>
  parseInt(getComputedStyle(heroImage).getPropertyValue(prop), 10);

const heroImageProperties = (heroImage) => ({
  heroImageBgPositionY: parsePropsInt("background-position-y", heroImage),
  heroImageWidth: parsePropsInt("width", heroImage),
  heroImageHeight: parsePropsInt("height", heroImage)
});

const getFormAction = () => document.querySelector("#redemption-form").getAttribute("action");

export default class extends Controller {
  static get targets() {
    return [
      "visualizerSection", "desktopBanner", "mobileBanner", "closeModalButton", "saveButton",
      "cancelButton", "replaceImageButton", "switchToDesktopButton", "switchToMobileButton"
    ];
  }

  connect() {
    this.imageCalculationCache = new Map();
    this.desktopHeroImageVisualization = this.desktopBannerTarget.querySelector(".hero-image-container");
    this.mobileHeroImageVisualization = this.mobileBannerTarget.querySelector(".hero-image-container");

    this.onRedemptionPageUpdated = this.onRedemptionPageUpdated.bind(this);
    this.onEditBannerModalOpen = this.onEditBannerModalOpen.bind(this);

    document.addEventListener("redemptionPageUpdated", this.onRedemptionPageUpdated);
    document.addEventListener("editBannerModalOpen", this.onEditBannerModalOpen);

    this.initializeImageEvents();
  }

  disconnect() {
    document.removeEventListener("redemptionPageUpdated", this.onRedemptionPageUpdated);
    document.removeEventListener("editBannerModalOpen", this.onEditBannerModalOpen);
  }

  useDefaultHeroHeight() {
    return this.visualizerSectionTarget.dataset.useDefaultHeight === "true";
  }

  blockButtons() {
    this.closeModalButtonTarget.classList.add("hidden");
    [this.saveButtonTarget, this.cancelButtonTarget, this.replaceImageButtonTarget,
      this.switchToDesktopButtonTarget, this.switchToMobileButtonTarget].forEach((button) => {
      button.disabled = true;
    });
  }

  unblockButtons() {
    this.closeModalButtonTarget.classList.remove("hidden");
    [this.saveButtonTarget, this.cancelButtonTarget, this.replaceImageButtonTarget,
      this.switchToDesktopButtonTarget, this.switchToMobileButtonTarget].forEach((button) => {
      button.disabled = false;
    });
  }

  updateRedemptionImageSettings(isMobile) {
    const path = getFormAction();
    const heroImage = isMobile ? this.mobileHeroImageVisualization : this.desktopHeroImageVisualization;
    const { heroImageBgPositionY, heroImageWidth, heroImageHeight } = heroImageProperties(heroImage);
    let params;

    if (isMobile) {
      const mobileOffsetRatio = heroImageBgPositionY / heroImageHeight;

      this.visualizerSectionTarget.dataset.mobileOffsetRatio = mobileOffsetRatio;
      params = { build: { mobile_offset_ratio: mobileOffsetRatio } };
    } else {
      const aspectRatio = Math.abs(heroImageHeight / heroImageWidth);
      const offsetRatio = heroImageBgPositionY / heroImageHeight;

      this.visualizerSectionTarget.dataset.aspectRatio = aspectRatio;
      this.visualizerSectionTarget.dataset.offsetRatio = offsetRatio;
      params = { build: { aspect_ratio: aspectRatio, offset_ratio: offsetRatio } };
    }

    const options = RequestHelper.defaultPatchOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify(params);

    fetch(path, options).then((response) => {
      if (!response.ok) { throw response; }
      // Reload the page, no real time for the moment
      window.location.reload();
    }).catch((error) => {
      window.ltAlert({
        message: "There was a problem updating the banner",
        type: "toast-error",
        duration: 2500,
        lasso: true
      });

      this.unblockButtons();
      console.warn(error);
    });
  }

  // --- Event Handlers ---

  setInitialCacheValues(imageProperties, eventType, originY, isMobile) {
    this.imageCalculationCache.set("imageProperties", imageProperties);
    this.imageCalculationCache.set("eventType", eventType);
    this.imageCalculationCache.set("originY", originY);
    this.imageCalculationCache.set("mouseDown", true);
    this.imageCalculationCache.set("isMobile", isMobile);
  }

  // Initial interaction, touch start
  onTouchResizeStart(e, heroImageVisualization) {
    this.setInitialCacheValues(
      heroImageProperties(heroImageVisualization),
      "resize",
      e.targetTouches ? e.targetTouches[0].clientY : e.clientY,
      false
    );

    heroImageVisualization.classList.add("resizing");
  }

  onTouchRepositionStart(e, heroImageVisualization, isMobile) {
    this.setInitialCacheValues(
      heroImageProperties(heroImageVisualization),
      "reposition",
      e.targetTouches ? e.targetTouches[0].clientY : e.clientY,
      isMobile
    );
  }

  // Adjustment interaction, mouse move or touch move
  onTouchMove(e, heroImageVisualization) {
    if (!this.imageCalculationCache.get("mouseDown")) { return; }

    const eventType = this.imageCalculationCache.get("eventType");
    const originY = this.imageCalculationCache.get("originY");
    const properties = this.imageCalculationCache.get("imageProperties");
    const isMobile = this.imageCalculationCache.get("isMobile");
    const scalingFactor = isMobile ? 1 : this.imageCalculationCache.get("desktopScale");
    const clientY = e.targetTouches ? e.targetTouches[0].clientY : e.clientY;

    if (eventType === "resize") {
      // We assume that resizing is done for desktop only
      let newHeight = properties.heroImageHeight + ((clientY - originY) * (1 / scalingFactor));

      if (newHeight <= MIN_HERO_HEIGHT) {
        newHeight = MIN_HERO_HEIGHT;
      }

      heroImageVisualization.style.maxHeight = `${newHeight}px`;
      const visualizationRealHeight = parseInt(heroImageVisualization.offsetHeight);

      if (visualizationRealHeight !== newHeight) {
        heroImageVisualization.style.maxHeight = `${visualizationRealHeight}px`;
      }

      // Also set the position of the resize button
      const resizeButton = this.desktopBannerTarget.querySelector(".btn-hero-image-resizer");
      if (resizeButton) {
        resizeButton.style.top = `${(visualizationRealHeight * scalingFactor) - (resizeButton.offsetHeight / 2)}px`;
      }

      // In the modal there's a helper text that also have to be repositioned
      const placeholderImage = this.desktopBannerTarget.querySelector(".placeholder-image");
      if (placeholderImage) {
        placeholderImage.style.top = `${visualizationRealHeight * scalingFactor}px`;
      }
    } else if (eventType === "reposition") {
      let newBgPositionY = properties.heroImageBgPositionY + ((clientY - originY) * (1 / scalingFactor));

      // Prevent image from moving down beyond the top of the container
      if (newBgPositionY > 0) {
        newBgPositionY = 0;
      }

      heroImageVisualization.style.backgroundPositionY = `${newBgPositionY}px`;
    }
  }

  // Final interaction, mouse up or touch end
  onTouchEnd(heroImageVisualization) {
    if (!this.imageCalculationCache.get("mouseDown")) { return; }
    if (this.imageCalculationCache.get("eventType") === "resize") {
      heroImageVisualization.classList.remove("resizing");
    }

    this.imageCalculationCache.delete("mouseDown");
  }

  addHeroImageEvents(visualizer, heroImageVisualization, isMobile) {
    heroImageVisualization.addEventListener("mousedown", (e) => this.onTouchRepositionStart(e, heroImageVisualization, isMobile));
    heroImageVisualization.addEventListener("touchstart", (e) => this.onTouchRepositionStart(e, heroImageVisualization, isMobile));
    // Attach the reposition event to the button
    const resizeButton = visualizer.querySelector(".btn-hero-image-resizer");
    if (resizeButton) {
      resizeButton.addEventListener("mousedown", (e) => this.onTouchResizeStart(e, heroImageVisualization));
      resizeButton.addEventListener("touchstart", (e) => this.onTouchResizeStart(e, heroImageVisualization));
    }
    // We attach these events to the visualizer since the user can move the resize outside the image
    visualizer.addEventListener("mousemove", (e) => this.onTouchMove(e, heroImageVisualization));
    visualizer.addEventListener("touchmove", (e) => this.onTouchMove(e, heroImageVisualization));
    visualizer.addEventListener("mouseup", () => this.onTouchEnd(heroImageVisualization));
    visualizer.addEventListener("mouseleave", () => this.onTouchEnd(heroImageVisualization));
    visualizer.addEventListener("touchend", () => this.onTouchEnd(heroImageVisualization));
  }

  calculateDesktopHeroImageScale() {
    // Read the width of the device viewport
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const scale = viewportWidth / DEFAULT_DESKTOP_WIDTH;

    this.imageCalculationCache.set("desktopScale", scale);

    this.desktopHeroImageVisualization.style.transform = `scale(${scale})`;
    this.desktopHeroImageVisualization.style.transformOrigin = "top left";

    // Set the position of the resize button
    const resizeButton = this.desktopBannerTarget.querySelector(".btn-hero-image-resizer");
    const currentHeroHeight = parseInt(this.desktopHeroImageVisualization.style.maxHeight) * scale;

    if (resizeButton) {
      resizeButton.style.top = `${currentHeroHeight - (resizeButton.offsetHeight / 2)}px`;
    }

    // In the modal there's a helper text that also have to be repositioned
    const placeholderImage = this.desktopBannerTarget.querySelector(".placeholder-image");
    if (placeholderImage) {
      placeholderImage.style.top = `${currentHeroHeight}px`;
    }
  }

  // Aspect ratio and resize logic
  calculateDefaultHeroImagePositionY(heroImageNaturalHeight, heroImageRatio, heroImageContainerWidth) {
    const scaledImageWidth = DEFAULT_HERO_HEIGHT * heroImageRatio;

    if (!this.useDefaultHeroHeight()) { return 0; }
    if (heroImageNaturalHeight <= DEFAULT_HERO_HEIGHT) { return 0; }
    if (scaledImageWidth >= heroImageContainerWidth) { return 0; }

    return (DEFAULT_HERO_HEIGHT - (heroImageContainerWidth / heroImageRatio)) / 2;
  }

  resize(defaultHeroPositionY, heroImageVisualization, heroImageRatio, visualizationWrapper) {
    const width = heroImageVisualization.offsetWidth;
    const aspectRatio = parseFloat(this.visualizerSectionTarget.dataset.aspectRatio);

    let offsetRatio = parseFloat(this.visualizerSectionTarget.dataset.offsetRatio);
    let mobileOffsetRatio = parseFloat(this.visualizerSectionTarget.dataset.mobileOffsetRatio);

    offsetRatio = Number.isNaN(offsetRatio) ? false : offsetRatio;
    mobileOffsetRatio = Number.isNaN(mobileOffsetRatio) ? 0 : mobileOffsetRatio;

    const useAspectRatio = visualizationWrapper.classList.contains("desktop-banner") ? aspectRatio : 1 / heroImageRatio; // we are not resizing mobile hero atm
    const useOffsetRatio = visualizationWrapper.classList.contains("desktop-banner") ? offsetRatio : mobileOffsetRatio;

    const maxHeight = (this.useDefaultHeroHeight() ? DEFAULT_HERO_HEIGHT : parseInt(width * useAspectRatio));
    const backgroundPositionY = parseInt(maxHeight * useOffsetRatio);

    if (offsetRatio !== false) {
      heroImageVisualization.style.backgroundPositionY = `${backgroundPositionY}px`;
    } else {
      heroImageVisualization.style.backgroundPositionY = `${defaultHeroPositionY}px`;
    }

    heroImageVisualization.style.maxHeight = `${maxHeight}px`;

    this.calculateDesktopHeroImageScale();
  }

  setHeroImageAspectRatio(visualizationWrapper, heroImageVisualization) {
    const img = new Image();
    const bgImage = getComputedStyle(heroImageVisualization).getPropertyValue("background-image");
    img.src = bgImage.replace(/"/g, "").replace(/url\(|\)$/ig, "");

    img.onload = () => {
      const heroImageNaturalWidth = img.naturalWidth;
      const heroImageNaturalHeight = img.naturalHeight;
      const heroImageRatio = heroImageNaturalWidth / heroImageNaturalHeight;

      const defaultHeroPositionY = this.calculateDefaultHeroImagePositionY(
        heroImageNaturalHeight, heroImageRatio, heroImageVisualization.offsetWidth
      );

      heroImageVisualization.style.aspectRatio = heroImageRatio;

      if (this.visualizerSectionTarget.dataset.aspectRatio === undefined) {
        this.visualizerSectionTarget.dataset.aspectRatio = heroImageRatio;
      }

      this.resize(defaultHeroPositionY, heroImageVisualization, heroImageRatio, visualizationWrapper);
    };
  }

  initializeImageEvents() {
    this.addHeroImageEvents(this.desktopBannerTarget, this.desktopHeroImageVisualization, false);
    // NOTE: Mobile hero image adjustments are disabled for now
    // this.addHeroImageEvents(this.mobileBannerTarget, this.mobileHeroImageVisualization, true);
  }

  // --- Stimulus actions ---

  closeModal() {
    this.element.classList.add("hidden");
    // Return body scroll
    document.querySelector("body").classList.remove("no-scroll");
  }

  switchToReplaceImage() {
    this.closeModal();
    document.querySelector(".edit-redemption-image-modal").classList.add("show");
  }

  switchToDesktop() {
    this.switchToDesktopButtonTarget.classList.add("active");
    this.switchToMobileButtonTarget.classList.remove("active");

    this.desktopBannerTarget.classList.remove("hidden");
    this.mobileBannerTarget.classList.add("hidden");
    this.setHeroImageAspectRatio(this.desktopBannerTarget, this.desktopHeroImageVisualization);
  }

  switchToMobile() {
    this.switchToDesktopButtonTarget.classList.remove("active");
    this.switchToMobileButtonTarget.classList.add("active");

    this.desktopBannerTarget.classList.add("hidden");
    this.mobileBannerTarget.classList.remove("hidden");
    this.setHeroImageAspectRatio(this.mobileBannerTarget, this.mobileHeroImageVisualization);
  }

  saveChanges() {
    // Prevent any action to happen
    this.blockButtons();
    this.updateRedemptionImageSettings(this.imageCalculationCache.get("isMobile"));
  }

  onEditBannerModalOpen() {
    // Remove the hidden class to show the modal
    this.element.classList.remove("hidden");
    // Prevent body scroll
    document.querySelector("body").classList.add("no-scroll");
    // Switch to desktop view by default
    this.switchToDesktop();
  }

  // Listen for redemption page updates to reapply the values
  onRedemptionPageUpdated() {
    // If the redemption page is updated, we replace the background image
    // to use the right one when the modal is opened

    // This desktop visualizer is outside the modal
    const desktopVisualizer = document.querySelector(".desktop-visualizer .hero-image-container");

    if (desktopVisualizer) {
      const newDesktopBgImage = getComputedStyle(desktopVisualizer).getPropertyValue("background-image");

      this.desktopHeroImageVisualization.style.backgroundImage = newDesktopBgImage;
      this.mobileHeroImageVisualization.style.backgroundImage = newDesktopBgImage;
    }
  }
}
